import { withAlpha } from '../../piano/render/pianoRenderColour';
import { GEM_CORNER_RADIUS_PX } from './guitarRenderConstants';

/**
 * Gem silhouette primitives extracted from {@link
 * import('./guitarDrawNotes').GuitarNoteRenderer} so the gem
 * appearance vocabulary (jewel shading, dual marker stripes, late-tap
 * danger pulse, native vs polyfilled rounded-rect) lives in one
 * focused file. Keeps the note renderer focused on perspective math +
 * approach-pulse / trail orchestration rather than per-gem paint
 * micro-details.
 *
 * All exports are pure module functions — no per-frame allocation, no
 * state. The note renderer is responsible for `ctx.save / restore`
 * outer transforms; these helpers only mutate the immediate fill /
 * stroke / shadow state.
 */

const HAS_NATIVE_ROUND_RECT =
  typeof CanvasRenderingContext2D !== 'undefined' &&
  typeof (CanvasRenderingContext2D.prototype as { roundRect?: unknown }).roundRect === 'function';

/**
 * One gem (tap or hold-head) drawn at (x, y) in CSS px with size
 * `width × height`. Diamond / rounded-rect hybrid — the gem reads as
 * a dimensional jewel rather than a flat pad. Dual gems get a thin
 * white tie mark across the top so the player reads "this gem is
 * one half of a chord" without relying on colour discrimination.
 *
 * `lightweightGems` collapses the gem to a flat fill + outline +
 * leading-edge highlight on low-tier devices, matching Piano's
 * lightweight-tile approach.
 */
export function drawGemShape(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  baseColour: string,
  accentColour: string,
  isDual: boolean,
  perspectiveScale: number,
  dangerPulse: number,
  lightweightGems: boolean,
  reducedMotion: boolean,
): void {
  const r = GEM_CORNER_RADIUS_PX * perspectiveScale;
  const cornerR = Math.min(r, width / 3, height / 3);

  ctx.save();
  if (lightweightGems) {
    ctx.fillStyle = baseColour;
    drawRoundedRect(ctx, x, y, width, height, cornerR);
    ctx.fill();
  } else {
    const gradient = ctx.createLinearGradient(0, y, 0, y + height);
    gradient.addColorStop(0, withAlpha(accentColour, 0.95));
    gradient.addColorStop(1, baseColour);
    if (!reducedMotion) {
      ctx.shadowColor = baseColour;
      ctx.shadowBlur = 10 + perspectiveScale * 8;
    }
    ctx.fillStyle = gradient;
    drawRoundedRect(ctx, x, y, width, height, cornerR);
    ctx.fill();
    ctx.shadowBlur = 0;
  }

  if (!lightweightGems) {
    ctx.fillStyle = withAlpha('#ffffff', 0.35);
    drawRoundedRect(
      ctx,
      x + width * 0.18,
      y + height * 0.12,
      width * 0.64,
      height * 0.28,
      cornerR * 0.6,
    );
    ctx.fill();
  }

  ctx.strokeStyle = withAlpha('#ffffff', 0.55);
  ctx.lineWidth = Math.max(1, perspectiveScale * 1.5);
  drawRoundedRect(ctx, x, y, width, height, cornerR);
  ctx.stroke();

  if (isDual) {
    const stripeW = width * 0.5;
    const stripeX = x + (width - stripeW) / 2;
    ctx.fillStyle = withAlpha('#ffffff', 0.7);
    ctx.fillRect(stripeX, y + height * 0.18, stripeW, 1.5);
    ctx.fillRect(stripeX, y + height * 0.32, stripeW, 1.5);
  }

  if (dangerPulse > 0) {
    ctx.fillStyle = withAlpha('#ff3b5c', 0.35 + dangerPulse * 0.35);
    drawRoundedRect(ctx, x, y, width, height, cornerR);
    ctx.fill();
  }
  ctx.restore();
}

/**
 * Crisp rounded-rect path using the native `roundRect` when the
 * runtime exposes it, falling back to an arc-based polyfill on older
 * browsers. Callers are expected to wrap the call in a
 * `ctx.beginPath / fill / stroke` cycle.
 */
export function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
): void {
  const r = Math.max(0, Math.min(radius, width / 2, height / 2));
  ctx.beginPath();
  if (HAS_NATIVE_ROUND_RECT) {
    ctx.roundRect(x, y, width, height, r);
    return;
  }
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + width - r, y);
  ctx.arcTo(x + width, y, x + width, y + r, r);
  ctx.lineTo(x + width, y + height - r);
  ctx.arcTo(x + width, y + height, x + width - r, y + height, r);
  ctx.lineTo(x + r, y + height);
  ctx.arcTo(x, y + height, x, y + height - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}
